<template>
  <div class="navbar">
    <ul class="navbar__links">
      <li>
        <router-link :to="{ name: 'Feed' }" class="navbar__link">
          <Text type="p" medium class="navbar__link-text">
            {{ $t('header.feed') }}
          </Text>
        </router-link>
      </li>
      <li v-if="isRewardsExist">
        <router-link :to="{ name: 'RewardsShop' }" class="navbar__link">
          <Text type="p" medium class="navbar__link-text">
            {{ $t('header.rewards') }}
          </Text>
        </router-link>
      </li>
      <li v-if="isGlobalAdmin || isAccoutAdmin">
        <router-link
          :to="{ name: 'Dashboard' }"
          class="navbar__link"
          @click="gtmEventTracker('dashboard_view', 'Dashboard_view', 'click', 'Dashboard view top menu')"
        >
          <Text type="p" medium class="navbar__link-text">
            {{ $t('header.dashboard') }}
          </Text>
        </router-link>
      </li>
    </ul>

    <NavMenu class="navbar__links-mobile" @switch="openSwitchWSModal" />

    <div class="actions">
      <RemoveDemo />
      <div class="navbar__dropdown" :class="{ navbar__dropdown_open: dropdownVisible }" @click.stop="toggleDropdown">
        <Avatar :img-src="currentUser?.avatar?.url" :name="currentUser?.name" size="36" font-size="16" />
        <div class="navbar__dropdown-btn" :class="{ 'navbar__dropdown-btn_open': dropdownVisible }" />
      </div>
    </div>

    <div
      ref="dropdown"
      class="navbar__dropdown-container"
      :class="{ 'navbar__dropdown-container_show': dropdownVisible }"
      @click.stop="hideDropdown"
    >
      <NavMenu @switch="openSwitchWSModal" />
    </div>
    <Modal ref="modalRef">
      <template #header>
        {{ $t('workspaces.modal.title') }}
      </template>
      <template #subtitle>
        {{ $t('workspaces.modal.count_ws', { count: teams.length }) }}
      </template>
      <template #body>
        <div class="workspaces">
          <TeamCard
            v-for="(team, index) in sortedTeams"
            :key="index"
            :name="team.name"
            :members-count="team.users_count"
            :is-active="team.id === workspace.id"
            :logo="team.logo.url || logoIcon"
            @click="onWorkspaceSelect(team.id)"
          />
        </div>
        <Text type="p">
          {{ $t(`workspaces.team_info`) }}
          <router-link :to="{ name: 'Feed' }" @click="onSignOut">
            {{ $t(`workspaces.login`) }}
          </router-link>
        </Text>
      </template>
      <template #footer>
        <Button :label="$t('workspaces.modal.cancel')" outline variant="secondary" @click="closeSwitchWSModal" />
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, watch, defineEmits, onMounted, onUnmounted, computed, inject } from 'vue';
import { Avatar, Modal, Text, Button } from '@sliday/karma-ui-library';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import TeamCard from '@/components/TeamCard.vue';
import logoIcon from '@/assets/logo.svg';
import NavMenu from './NavMenu.vue';
import gtmEventTracker from '@/utils/gtmEventTracker';
import RemoveDemo from '@/components/PageHeader/RemoveDemo.vue';

const store = useStore();
const router = useRouter();
const emits = defineEmits(['hideMenuIfMobile']);
const workspace = inject('workspace');

const isGlobalAdmin = computed(() => store.state.auth.permissions.global_administrator);
const isAccoutAdmin = computed(() => store.state.auth.permissions.account_administrator);

const currentUser = computed(() => store.state.auth.currentUser);
const teams = computed(() => store.state.auth.workspaces);

const dropdownVisible = ref(false);
const modalRef = ref(null);
const dropdown = ref(null);

const sortedTeams = computed(() => {
  let sorted = [...teams.value].sort((team) => team.users_count);
  const currentIndex = sorted.findIndex((team) => team.id === workspace.value.id);

  if (currentIndex !== -1) sorted = [...sorted.splice(currentIndex, 1), ...sorted];

  return sorted;
});

const isRewardsExist = computed(() => {
  return Object.keys(store.getters['shop/sortedRewards']).length;
});

watch(
  () => store.state.auth.workspaces,
  (value) => {
    teams.value = value;
  },
  { deep: true },
);

const toggleDropdown = () => {
  dropdownVisible.value = !dropdownVisible.value;
};

const openSwitchWSModal = () => {
  dropdownVisible.value = false;
  modalRef.value.show();
};

const closeSwitchWSModal = () => {
  modalRef.value.hide();
};

const hideDropdown = () => {
  dropdownVisible.value = false;
  emits('hideMenuIfMobile');
};

function onWorkspaceSelect(id) {
  try {
    if (modalRef.value) {
      closeSwitchWSModal();
    }
    router.push({ name: 'LoadingPage', query: { id: id } });
  } catch (e) {
    console.log(e);
  }
}

async function onSignOut() {
  try {
    await store.dispatch('auth/logout');
    router.push({ name: 'Login' });
  } catch (e) {
    console.log(e);
  }
}

onMounted(() => document.addEventListener('click', outsideClick));
onUnmounted(() => document.removeEventListener('click', outsideClick));
const outsideClick = (event) => {
  if (dropdown.value && !dropdown.value.contains(event.target) && dropdownVisible.value) {
    dropdownVisible.value = false;
  }
};
</script>

<style scoped lang="scss">
.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-left: 30px;
  flex: 1;
  position: relative;

  @include sm {
    position: absolute;
    top: 50px;
    left: 0;
    flex-direction: column;
    width: 100%;
    margin: 0;
    box-shadow:
      0px 4px 6px -2px rgba(16, 24, 40, 0.03),
      0px 12px 16px -4px rgba(16, 24, 40, 0.08);
    padding: 0;
    background-color: $white-color;
    align-items: start;
    z-index: 100;
  }

  &__links {
    display: flex;
    justify-content: space-between;
    column-gap: 30px;
    list-style: none;
    padding: 0;
    margin: 0;
    @include sm {
      flex-direction: column;
      margin-left: 0;
      padding-top: 12px;
      row-gap: 8px;
      justify-content: start;
      width: 100%;
      display: none;
    }

    &-mobile {
      display: none;
      @include sm {
        display: block;
      }
    }
  }
  &__link {
    display: flex;
    justify-content: center;
    align-items: center;
    text-decoration: none;
    height: 36px;

    @include sm {
      justify-content: start;
    }

    &:hover p {
      color: var(--color-active);
    }
  }
  &__link-text {
    @include sm {
      flex-direction: column;
    }
  }
  &__dropdown {
    display: flex;
    flex-direction: row;
    align-items: center;

    @include sm {
      margin: 8px 0;
      width: 100%;
      justify-content: space-between;
      display: none;
    }
  }
  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }
  &__dropdown-btn {
    width: 24px;
    height: 24px;
    background-color: transparent;
    background-image: url('./dropdown-icon.svg');
    cursor: pointer;

    &_open {
      transform: rotate(180deg);
    }
  }
  &__dropdown-container {
    display: none;
    width: 180px;
    padding: 4px 0;
    top: 44px;
    right: 0;
    position: absolute;
    border-radius: 4px;
    overflow: hidden;
    border: 1px solid $gray-border;
    background: #fff;
    box-shadow: $white-color;
    z-index: 1;

    @include md {
      top: 50px;
    }

    @include sm {
      position: relative;
      width: 100%;
      top: 0;
      border-radius: 12px;
      border: 1px solid var(--Gray-200, #eaecf0);
      background: var(--Base-White, #fff);
      box-shadow: 8px 8px 32px 0px rgba(0, 0, 0, 0.12);
    }

    &_show {
      display: block;
    }
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 32px;
  }
}
.workspaces {
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  margin-bottom: 16px;

  &__change-email {
    display: flex;
    flex-direction: row;
    column-gap: 2px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: $dark-text;
  }
  &__change-text {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: $dark-text;
  }
  &__change-btn {
    cursor: pointer;
    background-color: transparent;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: var(--color-active);
  }
}
</style>
